import React, { createContext, useContext, useEffect, useState } from "react";
import { getCurrentUser, fetchUserAttributes } from "aws-amplify/auth";
import { useLocation, useOutletContext } from "react-router-dom";
import {
  acceptChatLifecycle,
  denyChatLifecycle,
} from "../../api/communication/chat-lifecycle-service";
import {
  hideConversationForUser,
} from "../../api/communication/conversation-visibility-service";
import {
  publishCommunicationEvent,
  subscribeCommunicationEvents,
} from "../../api/communication/communication-events-service";
import { loadCommunicationInboxSnapshot } from "../../api/communication/communication-inbox-service";

function normalizeTimestampValue(value) {
  const normalized = String(value || "").trim();
  if (!normalized) return "";
  const asDate = new Date(normalized);
  if (!Number.isNaN(asDate.getTime())) return asDate.toISOString();
  return normalized;
}

function isNewerTimestamp(candidate, previous) {
  const normalizedCandidate = normalizeTimestampValue(candidate);
  const normalizedPrevious = normalizeTimestampValue(previous);
  if (!normalizedCandidate) return false;
  if (!normalizedPrevious) return true;
  return normalizedCandidate > normalizedPrevious;
}

// Formats Ping activity timestamps into short relative labels for request cards.
export function formatPingActivityTimeLabel(isoString) {
  const timestamp = new Date(isoString);
  if (Number.isNaN(timestamp.getTime())) return "";
  const now = Date.now();
  const diff = now - timestamp.getTime();
  const min = Math.floor(diff / 60000);
  if (min < 1) return "now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day}d ago`;
  const week = Math.floor(day / 7);
  return `${week}w ago`;
}

// Builds the Active chat route target used when a Ping request opens an accepted conversation.
export function buildPingChatNavigation(chatPk, title) {
  const slug = encodeURIComponent(title || "chat");
  return {
    pathname: `/active/chat/${slug}`,
    state: { selectedChatPk: chatPk },
  };
}

const PingActivityContext = createContext(null);

// Loads and coordinates Ping activity state, realtime request updates, and request actions.
export function PingActivityProvider({ children, outletContextOverride = null }) {
  const location = useLocation();
  const routeOutletContext = useOutletContext();
  const outletContext = outletContextOverride ?? routeOutletContext;
  const setHasPingNotifications = outletContext?.setHasPingNotifications;
  const communicationCache = outletContext?.communicationCache;
  const shellCurrentUser = outletContext?.currentUser;
  const isRequestsSurfaceOpen =
    location.pathname === "/ping" ||
    (location.pathname === "/connect" &&
      new URLSearchParams(location.search).get("view") === "requests");

  const [me, setMe] = useState(() => ({
    userId: shellCurrentUser?.userId || null,
    email: shellCurrentUser?.email || null,
    name: shellCurrentUser?.name || null,
  }));
  const [meLoading, setMeLoading] = useState(() => !Boolean(shellCurrentUser?.userId));
  const [receivedRequests, setReceivedRequests] = useState([]);
  const [myActionRequests, setMyActionRequests] = useState([]);
  const [acceptedRequests, setAcceptedRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(true);
  const inFlightRequestLoadRef = React.useRef(false);
  const pendingRequestReloadRef = React.useRef(false);
  const scheduledRequestReloadRef = React.useRef(null);
  const latestSyncedAtRef = React.useRef("");
  const latestAppliedActivityByChatRef = React.useRef(new Map());
  const recentInboxEventIdsRef = React.useRef(new Set());
  const acceptingChatRequestPromisesRef = React.useRef(new Map());
  const [acceptingChatRequestIds, setAcceptingChatRequestIds] = useState([]);

  useEffect(() => {
    if (!isRequestsSurfaceOpen) return;
    setHasPingNotifications?.(false);
  }, [isRequestsSurfaceOpen, setHasPingNotifications]);

  useEffect(() => {
    let cancelled = false;
    const shellUserId = String(shellCurrentUser?.userId || "").trim();

    if (shellUserId) {
      setMe({
        userId: shellUserId,
        email: shellCurrentUser?.email || null,
        name: shellCurrentUser?.name || null,
      });
      setMeLoading(false);
      return () => {
        cancelled = true;
      };
    }

    async function loadMe() {
      try {
        const user = await getCurrentUser();
        const attributes = await fetchUserAttributes();
        if (!cancelled) {
          setMe({
            userId: user?.userId ?? null,
            email: attributes?.email || null,
            name: attributes?.name || null,
          });
        }
      } catch (error) {
        console.error("Failed to load current user:", error);
      } finally {
        if (!cancelled) setMeLoading(false);
      }
    }

    loadMe();
    return () => {
      cancelled = true;
    };
  }, [shellCurrentUser?.email, shellCurrentUser?.name, shellCurrentUser?.userId]);

  const mySenderId = me.userId;

  useEffect(() => {
    if (!mySenderId) return;

    let cancelled = false;
    const MAX_RECENT_EVENT_IDS = 300;
    const FULL_REFRESH_STALE_MS = 5 * 60 * 1000;
    latestAppliedActivityByChatRef.current = new Map();
    recentInboxEventIdsRef.current = new Set();

    function rememberRecent(setRef, value, maxSize) {
      const normalized = String(value || "").trim();
      if (!normalized || setRef.has(normalized)) return false;
      setRef.add(normalized);
      if (setRef.size > maxSize) {
        const oldest = setRef.values().next().value;
        setRef.delete(oldest);
      }
      return true;
    }

    async function loadReceivedRequests({ silent = false, force = false } = {}) {
      if (inFlightRequestLoadRef.current) {
        pendingRequestReloadRef.current = true;
        return;
      }

      inFlightRequestLoadRef.current = true;
      if (!silent) setRequestsLoading(true);

      try {
        const snapshot = await loadCommunicationInboxSnapshot({
          userId: mySenderId,
          force,
          maxAgeMs: silent ? 4_000 : 0,
        });
        const requests = Array.isArray(snapshot?.pingReceivedRequests)
          ? snapshot.pingReceivedRequests
          : [];
        const accepted = Array.isArray(snapshot?.pingAcceptedRequests)
          ? snapshot.pingAcceptedRequests
          : [];
        const latestSyncedAt = String(snapshot?.latestSyncedAt || new Date().toISOString());

        if (!cancelled) {
          setReceivedRequests(requests);
          setAcceptedRequests(accepted);
          communicationCache?.setSnapshot?.("ping", mySenderId, {
            receivedRequests: requests,
            acceptedRequests: accepted,
            latestSyncedAt,
          });
        }
        latestSyncedAtRef.current = latestSyncedAt;
      } catch (error) {
        console.error("Failed to load received requests:", error);
        if (!cancelled) {
          setReceivedRequests([]);
          setAcceptedRequests([]);
          communicationCache?.setSnapshot?.("ping", mySenderId, {
            receivedRequests: [],
            acceptedRequests: [],
            latestSyncedAt: new Date().toISOString(),
          });
        }
      } finally {
        inFlightRequestLoadRef.current = false;

        if (pendingRequestReloadRef.current && !cancelled) {
          pendingRequestReloadRef.current = false;
          void loadReceivedRequests({ silent: true });
        }

        if (!cancelled && !silent) setRequestsLoading(false);
      }
    }

    function scheduleRequestReload({ immediate = false } = {}) {
      if (cancelled) return;
      if (scheduledRequestReloadRef.current) {
        clearTimeout(scheduledRequestReloadRef.current);
      }
      const delayMs = immediate ? 0 : 120;
      scheduledRequestReloadRef.current = setTimeout(() => {
        scheduledRequestReloadRef.current = null;
        void loadReceivedRequests({ silent: true });
      }, delayMs);
    }

    async function runCatchUpSync() {
      scheduleRequestReload({ immediate: true });
    }

    const unsubscribeInbox = subscribeCommunicationEvents((payload) => {
      const type = String(payload?.type || "");
      if (
        type !== "chat_request_created" &&
        type !== "chat_request_updated" &&
        type !== "chat_deleted" &&
        type !== "chat_visibility_changed"
      ) {
        return;
      }

      const eventId = String(payload?.eventId || "").trim();
      if (eventId) {
        const isNew = rememberRecent(
          recentInboxEventIdsRef.current,
          eventId,
          MAX_RECENT_EVENT_IDS
        );
        if (!isNew) return;
      }

      const chatPk = String(payload?.chatPk || "").trim();
      const activityAt = String(payload?.activityAt || payload?.sentAt || "").trim();
      if (chatPk && activityAt) {
        const latestAppliedAt = latestAppliedActivityByChatRef.current.get(chatPk) || "";
        if (!isNewerTimestamp(activityAt, latestAppliedAt)) {
          return;
        }
        latestAppliedActivityByChatRef.current.set(chatPk, normalizeTimestampValue(activityAt));
      }

      scheduleRequestReload();
    });

    const handleFocus = () => {
      if (document.visibilityState === "hidden") return;
      void runCatchUpSync();
    };
    const handleVisibility = () => {
      if (document.visibilityState !== "visible") return;
      void runCatchUpSync();
    };

    window.addEventListener("focus", handleFocus);
    window.addEventListener("online", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);

    const cachedSnapshot = communicationCache?.getSnapshot?.("ping", mySenderId);
    const cachedPayload = cachedSnapshot?.payload;
    const cachedReceivedRequests = Array.isArray(cachedPayload?.receivedRequests)
      ? cachedPayload.receivedRequests
      : [];
    const cachedAcceptedRequests = Array.isArray(cachedPayload?.acceptedRequests)
      ? cachedPayload.acceptedRequests
      : [];
    const hasCachedData = cachedReceivedRequests.length > 0 || cachedAcceptedRequests.length > 0;

    if (hasCachedData && !cancelled) {
      setReceivedRequests(cachedReceivedRequests);
      setAcceptedRequests(cachedAcceptedRequests);
      setRequestsLoading(false);
      latestSyncedAtRef.current = String(
        cachedPayload?.latestSyncedAt || cachedSnapshot?.updatedAt || ""
      );
    } else {
      void loadReceivedRequests();
    }

    const cacheUpdatedAtMs = new Date(cachedSnapshot?.updatedAt || "").getTime();
    const isCacheStale =
      !hasCachedData ||
      Number.isNaN(cacheUpdatedAtMs) ||
      Date.now() - cacheUpdatedAtMs > FULL_REFRESH_STALE_MS;

    if (hasCachedData && isCacheStale) {
      void loadReceivedRequests({ silent: true });
    }

    if (hasCachedData) {
      void runCatchUpSync();
    }

    return () => {
      cancelled = true;
      unsubscribeInbox();
      window.removeEventListener("focus", handleFocus);
      window.removeEventListener("online", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
      if (scheduledRequestReloadRef.current) {
        clearTimeout(scheduledRequestReloadRef.current);
        scheduledRequestReloadRef.current = null;
      }
      inFlightRequestLoadRef.current = false;
      pendingRequestReloadRef.current = false;
    };
  }, [mySenderId, communicationCache]);

  useEffect(() => {
    if (!mySenderId) return;
    if (requestsLoading && !receivedRequests.length && !acceptedRequests.length) return;
    communicationCache?.setSnapshot?.("ping", mySenderId, {
      receivedRequests,
      acceptedRequests,
      latestSyncedAt: latestSyncedAtRef.current || new Date().toISOString(),
    });
  }, [
    acceptedRequests,
    communicationCache,
    mySenderId,
    receivedRequests,
    requestsLoading,
  ]);

  const acceptChatRequest = async (chatId) => {
    const normalizedChatId = String(chatId || "").trim();
    if (!normalizedChatId) return false;

    const existingPromise = acceptingChatRequestPromisesRef.current.get(normalizedChatId);
    if (existingPromise) {
      await existingPromise;
      return true;
    }

    const previousReceivedRequests = receivedRequests;
    const previousAcceptedRequests = acceptedRequests;
    const previousMyActionRequests = myActionRequests;
    const acceptPromise = (async () => {
      setAcceptingChatRequestIds((previous) =>
        previous.includes(normalizedChatId) ? previous : [...previous, normalizedChatId]
      );
      const acceptedRequest = receivedRequests.find(
        (request) => request.chatPk === normalizedChatId
      );
      setReceivedRequests((previous) =>
        previous.filter((chat) => chat.chatPk !== normalizedChatId)
      );
      if (acceptedRequest) {
        const eventAt = new Date().toISOString();
        setMyActionRequests((previous) => [
          {
            id: `accepted-${normalizedChatId}-${eventAt}`,
            chatPk: normalizedChatId,
            title: acceptedRequest.title,
            profilePicture: acceptedRequest.profilePicture || "",
            status: "accepted-by-me",
            lastUpdatedAt: eventAt,
            timeLabel: formatPingActivityTimeLabel(eventAt),
            discussionTopic: acceptedRequest.discussionTopic || "",
            counterpartId: acceptedRequest.counterpartId || null,
            counterpartProfile: acceptedRequest.counterpartProfile || null,
          },
          ...previous,
        ].slice(0, 50));
      }

      await acceptChatLifecycle(normalizedChatId);
      publishCommunicationEvent({
        type: "chat_request_updated",
        chatPk: normalizedChatId,
        status: "accepted",
        activityAt: new Date().toISOString(),
      });
      return true;
    })();

    acceptingChatRequestPromisesRef.current.set(normalizedChatId, acceptPromise);

    try {
      return await acceptPromise;
    } catch (error) {
      setReceivedRequests(previousReceivedRequests);
      setAcceptedRequests(previousAcceptedRequests);
      setMyActionRequests(previousMyActionRequests);
      console.error("Error accepting chat request:", error);
      throw error;
    } finally {
      acceptingChatRequestPromisesRef.current.delete(normalizedChatId);
      setAcceptingChatRequestIds((previous) =>
        previous.filter((id) => id !== normalizedChatId)
      );
    }
  };

  const denyChatRequest = async (chatId) => {
    const previousReceivedRequests = receivedRequests;
    const previousAcceptedRequests = acceptedRequests;
    const previousMyActionRequests = myActionRequests;
    try {
      const deniedRequest = receivedRequests.find((request) => request.chatPk === chatId);
      setReceivedRequests((previous) => previous.filter((chat) => chat.chatPk !== chatId));
      setAcceptedRequests((previous) => previous.filter((chat) => chat.chatPk !== chatId));
      if (deniedRequest) {
        const eventAt = new Date().toISOString();
        setMyActionRequests((previous) => [
          {
            id: `denied-${chatId}-${eventAt}`,
            chatPk: chatId,
            title: deniedRequest.title,
            profilePicture: deniedRequest.profilePicture || "",
            status: "denied-by-me",
            lastUpdatedAt: eventAt,
            timeLabel: formatPingActivityTimeLabel(eventAt),
            discussionTopic: deniedRequest.discussionTopic || "",
            counterpartId: deniedRequest.counterpartId || null,
            counterpartProfile: deniedRequest.counterpartProfile || null,
          },
          ...previous,
        ].slice(0, 50));
      }

      await denyChatLifecycle(chatId);
      publishCommunicationEvent({
        type: "chat_request_updated",
        chatPk: chatId,
        status: "denied",
        activityAt: new Date().toISOString(),
      });
    } catch (error) {
      setReceivedRequests(previousReceivedRequests);
      setAcceptedRequests(previousAcceptedRequests);
      setMyActionRequests(previousMyActionRequests);
      console.error("Error denying chat request:", error);
    }
  };

  const hideConversation = async (chatId) => {
    if (!mySenderId || !chatId) return;
    const previousReceivedRequests = receivedRequests;
    const previousAcceptedRequests = acceptedRequests;
    const previousMyActionRequests = myActionRequests;
    try {
      setReceivedRequests((previous) => previous.filter((chat) => chat.chatPk !== chatId));
      setAcceptedRequests((previous) => previous.filter((chat) => chat.chatPk !== chatId));
      setMyActionRequests((previous) => previous.filter((chat) => chat.chatPk !== chatId));

      await hideConversationForUser({ userId: mySenderId, chatPk: chatId });
      publishCommunicationEvent({
        type: "chat_visibility_changed",
        chatPk: chatId,
        status: "hidden",
        activityAt: new Date().toISOString(),
      });
    } catch (error) {
      setReceivedRequests(previousReceivedRequests);
      setAcceptedRequests(previousAcceptedRequests);
      setMyActionRequests(previousMyActionRequests);
      console.error("Error hiding ping conversation:", error);
    }
  };

  return (
    <PingActivityContext.Provider
      value={{
        meLoading,
        me,
        requestsLoading,
        receivedRequests,
        myActionRequests,
        acceptedRequests,
        acceptingChatRequestIds,
        acceptChatRequest,
        denyChatRequest,
        hideConversation,
      }}
    >
      {children}
    </PingActivityContext.Provider>
  );
}

// Reads the Ping activity controller value and enforces provider usage.
export function usePingActivity() {
  const ctx = useContext(PingActivityContext);
  if (!ctx) throw new Error("usePingActivity must be used within PingActivityProvider");
  return ctx;
}
